import type { ThemeConfig } from 'antd';

import { theme as antdTheme } from 'antd';

interface SurfaceTokens {
  colorBgPanel: string;
  colorBgPopup: string;
  colorBorderOverlay: string;
  colorTextPopupTertiary: string;
  boxShadowPanel: string;
  boxShadowPopup: string;
}

declare module 'antd-style' {
  interface CustomToken extends SurfaceTokens {}
}

export function getSurfaceTokens(mode: 'light' | 'dark'): SurfaceTokens {
  return mode === 'dark' ? {
    colorBgPanel: '#48484c',
    colorBgPopup: '#54545a',
    colorBorderOverlay: '#73737a',
    colorTextPopupTertiary: '#c8c8d0',
    boxShadowPanel: '0 6px 20px rgba(0, 0, 0, 0.24), 0 2px 6px rgba(0, 0, 0, 0.18)',
    boxShadowPopup: '0 10px 32px rgba(0, 0, 0, 0.32), 0 3px 8px rgba(0, 0, 0, 0.20)',
  } : {
    colorBgPanel: '#ffffff',
    colorBgPopup: '#ffffff',
    colorBorderOverlay: '#d4d4d8',
    colorTextPopupTertiary: '#62626b',
    boxShadowPanel: '0 6px 20px rgba(24, 24, 27, 0.10), 0 2px 6px rgba(24, 24, 27, 0.06)',
    boxShadowPopup: '0 10px 32px rgba(24, 24, 27, 0.14), 0 3px 8px rgba(24, 24, 27, 0.08)',
  };
}

const lightSurface = getSurfaceTokens('light');
const darkSurface = getSurfaceTokens('dark');

const sharedToken: ThemeConfig['token'] = {
  colorSuccess: '#10b981',
  colorWarning: '#f59e0b',
  colorError: '#ef4444',

  fontSize: 14,
  fontSizeLG: 16,
  fontSizeSM: 13,
  fontSizeHeading1: 32,
  fontSizeHeading2: 24,
  fontSizeHeading3: 20,
  fontSizeHeading4: 17,
  fontSizeHeading5: 15,

  borderRadius: 8,
  borderRadiusLG: 12,
  borderRadiusSM: 4,
  borderRadiusXS: 2,
  controlHeight: 36,
  controlHeightLG: 44,
  controlHeightSM: 28,
  fontFamily:
    '"HarmonyOS Sans SC", "PingFang SC", "Microsoft YaHei UI", -apple-system, BlinkMacSystemFont, "Segoe UI", Inter, Roboto, "Helvetica Neue", Arial, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol"',
};

const sharedComponents: ThemeConfig['components'] = {
  Button: {
    paddingInline: 16,
    paddingInlineLG: 20,
    fontWeight: 500,
    primaryShadow: 'none',
    defaultShadow: 'none',
    dangerShadow: 'none',
  },
  Card: {
    headerBg: 'transparent',
    headerHeight: 52,
    headerHeightSM: 40,
    paddingLG: 20,
  },
  Input: { paddingBlock: 6, paddingInline: 12 },
  InputNumber: { paddingBlock: 6, paddingInline: 12 },
  Dropdown: {
    paddingBlock: 8,
    controlPaddingHorizontal: 12,
    borderRadiusSM: 8,
  },
  Menu: {
    itemBorderRadius: 8,
    itemMarginInline: 8,
    activeBarWidth: 0,
    activeBarBorderWidth: 0,
    iconMarginInlineEnd: 12,
  },
  Modal: { titleFontSize: 16, paddingContentHorizontalLG: 24 },
  Message: { contentPadding: '14px 22px' },
  Tabs: {
    titleFontSize: 14,
    horizontalItemGutter: 4,
    horizontalItemPadding: '8px 14px',
    inkBarColor: 'transparent',
  },
  Tooltip: { borderRadius: 8 },
  Alert: {
    borderRadiusLG: 12,
    defaultPadding: '10px 14px',
    withDescriptionPadding: '14px 16px',
  },
  Form: {
    labelFontSize: 13,
    verticalLabelPadding: '0 0 6px',
    itemMarginBottom: 20,
  },
  Table: { headerSplitColor: 'transparent' },
};

const lightToken: ThemeConfig['token'] = {
  ...sharedToken,
  colorPrimary: '#171717',
  colorPrimaryBg: '#f4f4f5',
  colorPrimaryBgHover: '#e4e4e7',
  colorPrimaryBorder: '#d4d4d8',
  colorPrimaryBorderHover: '#a1a1aa',

  colorInfo: '#171717',
  colorLink: '#171717',
  colorInfoBg: '#f4f4f5',
  colorInfoBgHover: '#e4e4e7',
  colorInfoBorder: '#d4d4d8',
  colorInfoBorderHover: '#a1a1aa',

  colorBgLayout: '#f7f7f8',
  colorBgContainer: '#ffffff',
  colorBgElevated: lightSurface.colorBgPanel,
  colorTextTertiary: '#62626b',
  colorTextPlaceholder: '#71717a',
  colorSuccessText: '#047857',
  colorWarningText: '#92400e',
  colorErrorText: '#b91c1c',
  colorBorder: '#d4d4d8',
  colorBorderSecondary: '#ececec',

  boxShadow: 'none',
  boxShadowSecondary: 'none',
  boxShadowTertiary: 'none',
};

function neutralAlertSurface(background: string, border: string) {
  return {
    colorInfoBg: background, colorSuccessBg: background,
    colorWarningBg: background, colorErrorBg: background,
    colorInfoBorder: border, colorSuccessBorder: border,
    colorWarningBorder: border, colorErrorBorder: border,
  };
}

const lightComponents: ThemeConfig['components'] = {
  Alert: neutralAlertSurface('rgba(0, 0, 0, 0.02)', '#ececec'),
  Typography: {
    colorSuccess: '#047857',
    colorWarning: '#92400e',
    colorError: '#b91c1c',
  },
  Form: { labelColor: '#3f3f46', colorError: '#b91c1c' },
  Switch: {
    colorTextQuaternary: '#71717a',
    colorTextTertiary: '#62626b',
  },
  Tag: {
    defaultBg: '#f4f4f5', defaultColor: '#3f3f46',
    colorSuccess: '#047857', colorWarning: '#92400e', colorError: '#b91c1c',
  },
  Button: {
    textHoverBg: 'rgba(0, 0, 0, 0.04)',
    defaultBorderColor: '#e5e5e5',
    defaultHoverBg: '#fafafa',
    defaultHoverBorderColor: '#d4d4d4',
    defaultHoverColor: '#171717',
    colorError: '#b91c1c',
    colorErrorHover: '#991b1b',
    colorErrorActive: '#7f1d1d',
  },
  Card: { boxShadowTertiary: 'none' },
  Input: {
    hoverBorderColor: '#d4d4d4',
    activeBorderColor: '#a1a1aa',
    activeShadow: 'none',
  },
  InputNumber: {
    hoverBorderColor: '#d4d4d4',
    activeBorderColor: '#a1a1aa',
    activeShadow: 'none',
  },
  Select: {
    activeBorderColor: '#a1a1aa',
    activeOutlineColor: 'transparent',
    optionSelectedBg: 'rgba(0, 0, 0, 0.06)',
    optionSelectedColor: '#171717',
    optionActiveBg: 'rgba(0, 0, 0, 0.04)',
  },
  Dropdown: {
    controlItemBgHover: 'rgba(0, 0, 0, 0.04)',
    controlItemBgActive: 'rgba(0, 0, 0, 0.06)',
    controlItemBgActiveHover: 'rgba(0, 0, 0, 0.08)',
    colorError: '#b91c1c',
    colorErrorHover: '#991b1b',
  },
  Menu: {
    popupBg: '#ffffff',
    darkPopupBg: '#3a3a3d',
    dangerItemColor: '#b91c1c',
    dangerItemSelectedColor: '#b91c1c',
    itemSelectedBg: 'rgba(0, 0, 0, 0.06)',
    itemSelectedColor: '#171717',
    itemHoverBg: 'rgba(0, 0, 0, 0.04)',
    itemHoverColor: '#171717',
    horizontalItemSelectedColor: '#171717',
    horizontalItemHoverColor: '#171717',
  },
  Tooltip: { colorBgSpotlight: 'rgba(38, 38, 38, 0.95)' },
  Segmented: {
    trackBg: '#f4f4f5',
    itemColor: '#62626b',
    itemHoverColor: '#62626b',
    itemHoverBg: 'transparent',
    itemSelectedBg: '#ffffff',
    itemSelectedColor: '#171717',
  },
  Message: { contentBg: '#ffffff' },
  Tabs: {
    itemColor: '#71717a',
    itemSelectedColor: '#171717',
    itemActiveColor: '#171717',
    itemHoverColor: '#71717a',
  },
  Table: {
    headerBg: '#fafafa',
    headerColor: '#3f3f46',
    rowHoverBg: '#f4f4f5',
    borderColor: '#f0f0f0',
  },
};

const darkToken: ThemeConfig['token'] = {
  ...sharedToken,
  colorPrimary: '#fafafa',
  colorPrimaryHover: '#e4e4e7',
  colorPrimaryActive: '#d4d4d8',
  colorPrimaryBg: 'rgba(255, 255, 255, 0.14)',
  colorPrimaryBgHover: 'rgba(255, 255, 255, 0.20)',
  colorPrimaryBorder: '#56565a',
  colorPrimaryBorderHover: '#73737a',

  colorInfo: '#fafafa',
  colorLink: '#d4d4d8',

  colorText: '#fafafa',
  colorTextSecondary: '#d4d4d8',
  colorTextTertiary: '#b8b8c0',
  colorTextQuaternary: '#8f8f99',
  colorTextPlaceholder: '#b8b8c0',
  colorSuccessText: '#6ee7b7',
  colorWarningText: '#fcd34d',
  colorErrorText: '#ffa3a3',

  colorBgLayout: '#0a0a0c',
  colorBgContainer: '#3a3a3d',
  colorBgElevated: darkSurface.colorBgPanel,
  colorBgContainerDisabled: '#323235',
  colorBorder: '#56565a',
  colorBorderSecondary: '#4a4a4d',

  colorFill: 'rgba(255, 255, 255, 0.18)',
  colorFillSecondary: 'rgba(255, 255, 255, 0.13)',
  colorFillTertiary: 'rgba(255, 255, 255, 0.08)',
  colorFillQuaternary: 'rgba(255, 255, 255, 0.05)',
  colorTextDisabled: '#777782',

  controlItemBgHover: 'rgba(255, 255, 255, 0.06)',
  controlItemBgActive: 'rgba(255, 255, 255, 0.10)',
  controlItemBgActiveHover: 'rgba(255, 255, 255, 0.13)',

  boxShadow: 'none',
  boxShadowSecondary: 'none',
  boxShadowTertiary: 'none',
};

const darkComponents: ThemeConfig['components'] = {
  Alert: neutralAlertSurface('rgba(255, 255, 255, 0.05)', '#4a4a4d'),
  Steps: {
    colorTextLightSolid: '#171717',
    colorError: '#ffa3a3',
    colorErrorHover: '#ffbdbd',
  },
  Typography: {
    colorSuccess: '#6ee7b7',
    colorWarning: '#fcd34d',
    colorError: '#ffa3a3',
  },
  Form: { labelColor: '#d4d4d8', colorError: '#ffa3a3' },
  // The primary fill is pale in dark mode; selection marks need a dark ink.
  Checkbox: { colorWhite: '#171717' },
  Switch: {
    colorTextQuaternary: '#56565a',
    colorTextTertiary: '#626269',
  },
  Tag: {
    defaultBg: 'rgba(255, 255, 255, 0.08)', defaultColor: '#e4e4e7',
    colorSuccess: '#6ee7b7', colorWarning: '#fcd34d', colorError: '#ffa3a3',
    colorTextLightSolid: '#171717',
  },
  Button: {
    textHoverBg: 'rgba(255, 255, 255, 0.06)',
    defaultBorderColor: '#56565a',
    defaultHoverBg: '#464649',
    defaultHoverBorderColor: '#73737a',
    defaultHoverColor: '#ffffff',
    primaryColor: '#171717',
    colorError: '#ffa3a3',
    colorErrorHover: '#ffbdbd',
    colorErrorActive: '#ffa3a3',
    dangerColor: '#171717',
  },
  Card: { boxShadowTertiary: 'none' },
  Input: {
    hoverBorderColor: '#73737a',
    activeBorderColor: '#a1a1aa',
    activeShadow: 'none',
  },
  InputNumber: {
    hoverBorderColor: '#73737a',
    activeBorderColor: '#a1a1aa',
    activeShadow: 'none',
  },
  Select: {
    activeBorderColor: '#a1a1aa',
    activeOutlineColor: 'transparent',
    optionSelectedBg: 'rgba(255, 255, 255, 0.10)',
    optionSelectedColor: '#ffffff',
    optionActiveBg: 'rgba(255, 255, 255, 0.06)',
  },
  Dropdown: {
    controlItemBgHover: 'rgba(255, 255, 255, 0.08)',
    controlItemBgActive: 'rgba(255, 255, 255, 0.10)',
    controlItemBgActiveHover: 'rgba(255, 255, 255, 0.13)',
    colorError: '#ffa3a3',
    colorErrorHover: '#ffbdbd',
  },
  Menu: {
    popupBg: '#48484c',
    darkPopupBg: '#48484c',
    darkItemSelectedBg: 'rgba(255, 255, 255, 0.10)',
    darkItemSelectedColor: '#fafafa',
    darkItemHoverBg: 'rgba(255, 255, 255, 0.06)',
    dangerItemColor: '#ffa3a3',
    dangerItemSelectedColor: '#ffa3a3',
    itemSelectedBg: 'rgba(255, 255, 255, 0.10)',
    itemSelectedColor: '#ffffff',
    itemHoverBg: 'rgba(255, 255, 255, 0.06)',
    itemHoverColor: '#ffffff',
    horizontalItemSelectedColor: '#ffffff',
    horizontalItemHoverColor: '#ffffff',
  },
  Tooltip: { colorBgSpotlight: 'rgba(32, 32, 34, 0.98)' },
  Segmented: {
    trackBg: 'rgba(255, 255, 255, 0.08)',
    itemColor: '#d4d4d8',
    itemHoverColor: '#ffffff',
    itemHoverBg: 'rgba(255, 255, 255, 0.05)',
    itemSelectedBg: '#f4f4f5',
    itemSelectedColor: '#171717',
  },
  Radio: {
    colorWhite: '#171717',
    buttonSolidCheckedBg: '#f4f4f5',
    buttonSolidCheckedHoverBg: '#ffffff',
    buttonSolidCheckedActiveBg: '#e4e4e7',
    buttonSolidCheckedColor: '#171717',
  },
  Message: { contentBg: '#48484c' },
  Tabs: {
    itemColor: '#d4d4d8',
    itemSelectedColor: '#ffffff',
    itemActiveColor: '#ffffff',
    itemHoverColor: '#ffffff',
  },
  Table: {
    headerBg: '#464649',
    headerColor: '#f4f4f5',
    rowHoverBg: 'rgba(255, 255, 255, 0.06)',
    borderColor: '#56565a',
  },
};

function mergeComponents(
  base: ThemeConfig['components'] = {},
  overrides: ThemeConfig['components'] = {},
): ThemeConfig['components'] {
  const out: ThemeConfig['components'] = { ...base };
  for (const [key, value] of Object.entries(overrides)) {
    out[key as keyof typeof out] = {
      ...(out[key as keyof typeof out] as object | undefined),
      ...(value as object),
    } as never;
  }
  return out;
}

function overlayComponents(mode: 'light' | 'dark'): ThemeConfig['components'] {
  const surface = getSurfaceTokens(mode);
  const popup = {
    colorBgElevated: surface.colorBgPopup,
    colorTextTertiary: surface.colorTextPopupTertiary,
    boxShadowSecondary: surface.boxShadowPopup,
  };
  // Drawer consumes these directional shadows at runtime; its public token type omits them.
  const drawer = {
    colorBgElevated: surface.colorBgPanel,
    colorBorder: surface.colorBorderOverlay,
    boxShadowDrawerLeft: surface.boxShadowPanel,
    boxShadowDrawerRight: surface.boxShadowPanel,
    boxShadowDrawerUp: surface.boxShadowPanel,
    boxShadowDrawerDown: surface.boxShadowPanel,
  };
  return {
    Popover: { ...popup, colorBorder: surface.colorBorderOverlay },
    Popconfirm: { ...popup, colorBorder: surface.colorBorderOverlay },
    Dropdown: { ...popup, colorBorder: surface.colorBorderOverlay },
    Select: popup,
    TreeSelect: popup,
    Cascader: popup,
    Mentions: popup,
    DatePicker: popup,
    ColorPicker: popup,
    Tooltip: { boxShadowSecondary: surface.boxShadowPanel },
    Menu: {
      popupBg: surface.colorBgPopup,
      darkPopupBg: surface.colorBgPopup,
      colorBorder: surface.colorBorderOverlay,
      colorTextTertiary: popup.colorTextTertiary,
      boxShadowSecondary: surface.boxShadowPopup,
    },
    Modal: {
      contentBg: surface.colorBgPanel,
      colorBorder: surface.colorBorderOverlay,
      headerBg: 'transparent',
      footerBg: 'transparent',
      boxShadow: surface.boxShadowPopup,
    },
    Drawer: drawer,
    Message: {
      contentBg: surface.colorBgPanel,
      colorBorder: surface.colorBorderOverlay,
      boxShadowTertiary: surface.boxShadowPanel,
    },
    Notification: {
      colorBgElevated: surface.colorBgPanel,
      colorBorder: surface.colorBorderOverlay,
      boxShadow: surface.boxShadowPanel,
    },
  };
}

export function buildThemeConfig(mode: 'light' | 'dark'): ThemeConfig {
  if (mode === 'dark') {
    return {
      algorithm: antdTheme.darkAlgorithm,
      token: darkToken,
      components: mergeComponents(mergeComponents(sharedComponents, darkComponents), overlayComponents(mode)),
      cssVar: { key: 'qm-theme' },
    };
  }
  return {
    algorithm: antdTheme.defaultAlgorithm,
    token: lightToken,
    components: mergeComponents(mergeComponents(sharedComponents, lightComponents), overlayComponents(mode)),
    cssVar: { key: 'qm-theme' },
  };
}
